import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Stack,
  Textarea,
  Tooltip,
} from "@altimateai/lego";
import styles from "./styles.module.scss";
import classnames from "classnames";
import dayjs from "dayjs";
import { ColumnDatatype } from "../../components/column";
import { getIconByDatastoreType } from "./DatastoreImage";
import { useMemo, useState, useEffect } from "react";
import { ColorTag, ModifiedByTag } from "../../components/tags";
import { default as EditIcon } from "@/assets/icons/edit.svg?react";
import { useBoolean } from "../../helpers/hooks";
import { DebouncedInput } from "../../components/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getTableRk, updateTableSchemaDesc } from "@/helpers/apis";
import { GET_TABLES } from "../../services/filters";
import { scroll_style } from "../../components/scrollContainer";
import { useTenantUsers } from "../../services/common";
import { PurposeSection } from "../../components/lineage";
import { GET_TABLE_RK } from "./DatasetsDetails";
import { RelativeComponentLoader } from "../../components/loader";
import { TableItem, Column, Contract } from "./types";
import { UserListResponse } from "@/types";
import { getTenant } from "@/helpers/tenant";

const colors = ["blue", "orange", "yellow", "purple", "dark_green"];
const colorsMap: Record<string, string> = {};
let i = 0;

interface ColumnCardProps {
  column: Column;
  users: UserListResponse["users"];
  colDescription?: string | null;
}

const ColumnCard = ({ column, users, colDescription }: ColumnCardProps): JSX.Element => {
  const { value: isEditing, setTrue, setFalse } = useBoolean();
  const [description, setDescription] = useState<string | null>(null);
  useEffect(() => {
    setDescription(colDescription || null);
  }, [colDescription]);
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: (data: { description: string | null }) =>
      updateTableSchemaDesc(column.table_schema_id, data),
    onSuccess: (data?: { ok: boolean }) => {
      if (data?.ok) {
        setFalse();
        queryClient.invalidateQueries({ queryKey: [GET_TABLES] });
      }
    },
  });

  return (
    <div className={styles.column_card}>
      <Stack align={"center"} gap={"none"}>
        <ColumnDatatype datatype={column.datatype} />
        <div className="ms-2">{column.name}</div>
        <div className="spacer" />
        <ModifiedByTag users={users} lastModifiedBy={column.last_modified_by} />
      </Stack>
      <ColorTag label={column.datatype} color={styles.column_tag} />
      <div className="mb-1 divider" />
      <Stack align={"center"} gap={"xs"}>
        <div>Description</div>
        <EditIcon
          className={classnames("cursor-pointer", styles.edit_icon, {
            [styles.active]: isEditing,
          })}
          onClick={(e: React.MouseEvent<SVGSVGElement>) => {
            e.stopPropagation();
            if (isEditing) {
              setFalse();
              setDescription(colDescription || "");
            } else {
              setTrue();
            }
          }}
        />
      </Stack>
      {isEditing ? (
        <>
          <Textarea
            className={classnames("mb-3", styles.multiline_input)}
            value={description || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
            placeholder="Enter the column description"
          />
          <Button
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              mutate({ description });
            }}
          >
            Update
          </Button>
        </>
      ) : (
        <div className="text-muted font-normal">{description}</div>
      )}
      <div className="al-flex gap-sm flex-wrap">
        {(column.tags || []).map(t => {
          if (!colorsMap[t.name]) colorsMap[t.name] = colors[i++ % colors.length];
          return (
            <Tooltip title={t.description} key={t.name}>
              <ColorTag color={colorsMap[t.name]} label={t.name} />
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

interface ContractSectionProps {
  contracts: Contract[];
  total_violations?: number;
  last_violation_timestamp?: string | null;
}

const ContractSection = ({
  contracts,
  total_violations,
  last_violation_timestamp,
}: ContractSectionProps): JSX.Element => {
  return (
    <Card className={classnames("mb-0", styles.bg_gray)}>
      <CardContent className="al-pt-4">
        <div className="fs-5 fw-semibold">Contract</div>
        <div className="al-flex mt-2 mb-2">
          <div className="me-2">Contracts:</div>
          <div className="text-primary">
            {[...new Set((contracts || []).map(c => c.name))].join(", ")}
          </div>
        </div>
        <div className="al-flex mt-2 mb-2">
          <div className="me-2">Last Validation:</div>
          <div>
            <span className={classnames("text-underline me-2", styles.orange)}>
              {total_violations} Incidents
            </span>
            {last_violation_timestamp && (
              <span className="text-primary">
                ({dayjs(last_violation_timestamp).format("DD MMM YYYY, hh:mm A")})
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface ColumnSectionProps {
  columns: Column[];
  users: UserListResponse["users"];
}

const ColumnSection = ({ columns, users }: ColumnSectionProps): JSX.Element => {
  const [search, setSearch] = useState<string>("");
  const safeColumns = columns || [];
  const filteredColumns = useMemo(() => {
    if (!search) return safeColumns;
    const _search = search.toLowerCase();
    return safeColumns.filter(c => c.name?.includes(_search));
  }, [safeColumns, search]);

  return (
    <Card className={classnames("mb-0", styles.bg_gray)} aria-label="Schema columns list">
      <CardContent className="al-flex al-flex-col gap-sm al-h-full al-pt-4">
        <div className="fs-5 fw-semibold mb-2">Columns</div>
        <DebouncedInput
          placeholder="Search Column"
          aria-label="Search Column"
          onChange={setSearch}
        />
        {search ? (
          <div className="text-primary">
            {filteredColumns.length} {filteredColumns.length === 1 ? "result" : "results"} found
          </div>
        ) : (
          <div>{safeColumns.length} columns</div>
        )}
        <Stack direction={"column"} gap={"sm"}>
          {filteredColumns.map(c => (
            <ColumnCard
              key={c.table_schema_id}
              column={c}
              users={users}
              colDescription={c.column_description}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

interface HeaderSectionProps {
  table: TableItem;
  onDetailsClick?: (table: TableItem) => void;
}

const HeaderSection = ({ table, onDetailsClick }: HeaderSectionProps): JSX.Element => {
  return (
    <div className="al-flex al-items-center">
      {getIconByDatastoreType(table.datasource_type, "me-2 icon-md")}
      <div className={styles.column_table_name}>{table.name}</div>
      <div className="spacer" />
      {onDetailsClick && (
        <Button
          className="mw-8"
          size="sm"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onDetailsClick(table);
          }}
        >
          View Details
        </Button>
      )}
    </div>
  );
};

interface ColumnsModalProps {
  table: TableItem | null;
  onDetailsClick?: (table: TableItem) => void;
}

export function ColumnsModal({ table, onDetailsClick }: ColumnsModalProps): JSX.Element | null {
  const { users = [] } = useTenantUsers();

  // Early return if table is null/undefined (can happen during Sheet close animation)
  const { data: tableRk, isLoading: isTableRkLoading } = useQuery({
    queryKey: [GET_TABLE_RK, table?.id],
    queryFn: () => getTableRk(table!.id),
    enabled: !!table?.id && getTenant() !== "apptio",
  });

  if (!table) return null;

  if (getTenant() === "apptio") {
    return (
      <div className={classnames("al-flex al-flex-col al-h-full p-3 gap-md m-1", scroll_style)}>
        <HeaderSection table={table} onDetailsClick={onDetailsClick} />
        <Alert variant="destructive">
          <AlertTitle>Schema not available</AlertTitle>
          <AlertDescription>Schema information is not available for this table.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isTableRkLoading) {
    return <RelativeComponentLoader label={"Loading columns..."} />;
  }

  return (
    <div className={classnames("al-flex al-flex-col al-h-full p-3 gap-md m-1", scroll_style)}>
      <HeaderSection table={table} onDetailsClick={onDetailsClick} />
      {table.datasource_type !== "postgres" && table.contracts && (
        <ContractSection
          contracts={table.contracts}
          total_violations={table.total_violations}
          last_violation_timestamp={table.last_violation_timestamp}
        />
      )}
      {tableRk?.table_rk && (
        <PurposeSection
          table={tableRk.table_rk}
          tableId={table.id}
          purpose={table.purpose}
          users={users}
          lastModifiedBy={table.last_modified_by}
        />
      )}
      <ColumnSection columns={table.columns} users={users} />
    </div>
  );
}
